import { useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { CgSpinner } from "react-icons/cg";
import NodeInstance from "../../../../Global/nodeAxios";
import { setAppError } from "../../../../store/userSlice";
import useServerPagination from "../../../../hooks/useServerPagination";
import PaginationControls from "../../../../components/SmartDataTable/PaginationControls";
import SearchFilter from "../../../../components/SmartDataTable/SearchFilter";



const formatDate = (dateStr) => {
  if (!dateStr) return "-----";
  return new Date(dateStr).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).split(" ").join("-").toUpperCase();
};

const isVisaExpiringSoon = (expiryDate) => {
  if (!expiryDate) return false;
  return Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 3600 * 24)) < 90;
};

const HEADERS = [
  "S.NO", "VRF REFERENCE NO", "VISA PROVIDER NAME", "VISA PROFESSION",
  "NATIONALITY", "STAMP CITY", "VISA BLOCK NUMBER", "PAYMENT DATE",
  "SPONSOR ID", "TOTAL VISAS", "VISA ISSUE DATE", "BLOCK ISSUE DATE", "BLOCK EXPIRY DATE",
];

const NAV_BASE = "p-2 px-4 rounded-md text-white text-sm font-medium";
const NAV_ACTIVE = { backgroundColor: "#1f2937" };
const NAV_INACTIVE = { backgroundColor: "#ef4444" };
const navLink = (isActive) => ({ className: NAV_BASE, style: isActive ? NAV_ACTIVE : NAV_INACTIVE });

export default function VisaStocklistView({ titles = [] }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const token = useMemo(() => localStorage.getItem("nodeToken"), []);
  const [searchTerm, setSearchTerm] = useState("");
  const [downloadOption, setDownloadOption] = useState("all");
  const [downloadLoading, setDownloadLoading] = useState(false);

  const displayTitles = titles.length > 0 ? titles : HEADERS;

  const { data, paginationState, goToPage, changePageSize } = useServerPagination({
    apiEndpoint: "/dashboard/vrf-visa",
    token,
    defaultPageSize: 50,
    searchTerm,
    searchDebounceMs: 300,
  });

  const downloadFile = async () => {
    setDownloadLoading(true);
    try {
      const params = new URLSearchParams();
      if (downloadOption === "current") {
        params.append("page", paginationState.currentPage);
        params.append("limit", paginationState.pageSize);
      }
      const query = params.toString() ? `?${params.toString()}` : "";
      const response = await NodeInstance.get(`/dashboard/vrf-visa/downloadVrfSummaryExcel${query}`, { responseType: "blob" });
      if (response.status === 200) {
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `VISA-STOCK-SUMMARY`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        dispatch(setAppError({ msg: "Excel Downloaded", color: "success" }));
      }
    } catch (err) {
      dispatch(setAppError({ msg: err.message }));
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <div className="p-2 uppercase">

      {/* Toolbar */}
      <div className="flex-wrap gap-2 w-full items-center flex flex-row-reverse mb-4">
        <SearchFilter
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          placeholder="Search across all columns..."
          isLoading={paginationState.isLoading && searchTerm.length > 0}
        />
        <div className="flex gap-2 items-center">
          <select
            className={`select select-sm select-bordered focus:outline-none ${paginationState.isLoading || downloadLoading ? "cursor-not-allowed opacity-50 bg-gray-100" : "cursor-pointer"}`}
            value={downloadOption}
            onChange={(e) => setDownloadOption(e.target.value)}
            disabled={paginationState.isLoading || downloadLoading}
          >
            <option value="all">All Results ({paginationState.totalRecords})</option>
            <option value="current">Current Page ({data.length})</option>
          </select>
          <button
            className="p-2 px-4 rounded-md bg-blue disabled:bg-gray-500 text-white min-w-fit text-sm flex items-center gap-2"
            onClick={downloadFile}
            disabled={downloadLoading || paginationState.isLoading}
          >
            {downloadLoading && <CgSpinner className="animate-spin" />}
            <span>{downloadLoading ? "Downloading..." : "Download"}</span>
          </button>
        </div>
        <Link to="/dashboard/visa-stock-details" {...navLink(location.pathname.includes("visa-stock-details"))}>VISA STOCK DETAILS</Link>
        <span {...navLink(!location.pathname.includes("visa-stock-details"))}>VRF SUMMARY</span>
      </div>

      {/* Scrollable Table */}
      <div className="overflow-x-auto bg-white rounded-lg shadow max-h-[70vh]">
        <table className="min-w-full">
          <thead className="sticky top-0 z-20 bg-white border-b border-slate-200">
            <tr>
              {displayTitles.map((t, i) => (
                <th key={i} className="px-6 py-4 text-left text-base font-semibold text-gray-700 uppercase tracking-wider">
                  {t}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white">
            {paginationState.isLoading ? (
              <tr>
                <td colSpan={displayTitles.length} className="px-6 py-8 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <CgSpinner className="animate-spin text-xl text-blue-500" />
                    <span>Loading data...</span>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={displayTitles.length} className="px-6 py-8 text-center text-gray-400">
                  {searchTerm ? `No results found for "${searchTerm}"` : "No Data Found"}
                </td>
              </tr>
            ) : data.map((listItem, index) => {
              const expiring = isVisaExpiringSoon(listItem.visa_block_expiry_date);
              return (
                <tr key={listItem.id || index} className={`${expiring ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{(paginationState.currentPage - 1) * paginationState.pageSize + index + 1}</td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{listItem?.vrf_reference_number}</td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{listItem?.company_name}</td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{listItem?.visa_profession}</td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{listItem?.Nationality}</td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{listItem?.visa_stamp_city}</td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{listItem?.visa_block_number || "--"}</td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{formatDate(listItem?.payment_done_date)}</td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{listItem?.sponsor_id}</td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{listItem?.total_visa}</td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{formatDate(listItem?.visa_issue_date)}</td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{formatDate(listItem?.visa_block_issue_date)}</td>
                  <td className={`px-6 py-6 whitespace-nowrap text-base ${expiring ? 'text-red font-semibold' : 'text-gray-700'}`}>{formatDate(listItem?.visa_block_expiry_date)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="mt-2">
        <PaginationControls
          currentPage={paginationState.currentPage}
          totalPages={paginationState.totalPages}
          pageSize={paginationState.pageSize}
          totalRecords={paginationState.totalRecords}
          onPageChange={goToPage}
          onPageSizeChange={changePageSize}
          isLoading={paginationState.isLoading}
        />
      </div>
    </div>
  );
}
